// src/components/Level1.tsx

import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
//import Sidebar from "./Sidebar";
import CheckupQuiz from "./CheckupQuiz"; // <-- Dieser Import ist korrekt und sollte so bleiben
import * as THREE from 'three'; // <-- Diesen Import benötigst du eventuell nicht, falls THREE nicht verwendet wird, aber er verursacht keinen Syntaxfehler hier.

const MODEL_URL = "https://teachablemachine.withgoogle.com/models/yl4fN-mF-/model.json";
const METADATA_URL = "https://teachablemachine.withgoogle.com/models/yl4fN-mF-/metadata.json";

type Prediction = {
    label: string;
    confidence: number;
};
type Props = {
    onBack: () => void;
    onNextLevel: () => void;
    onRestart: () => void;
    avatarImage: string;
    groupName: string;
    initialLevelStep?: number; // <--- NEU
};


type Snapshot = {
    imageSrc: string;
    predictions: Prediction[];
};

type CategorySnapshots = {
    [categoryName: string]: Snapshot[];
};



// Fragen für das Checkup-Quiz dieses Levels
const quizQuestion1 = {
    question: "Warum teilt ihr eure Fotos in die zwei Kategorien \"Äpfel\" und \"Bananen\"?",
    options: [
        { label: "A) Damit die Kamera schneller arbeitet.", value: "A" },
        { label: "B) Damit die Farben der Bilder besser zur Geltung kommen.", value: "B" },
        { label: "C) Damit lernt die KI, die Bilder in die richtigen Gruppen einzuordnen.", value: "C" },
    ],
    correctAnswer: "C",
    explanation: "Indem wir Bilder kategorisieren, lernt die KI Muster und Merkmale, um ähnliche Objekte zukünftig selbstständig zu erkennen."
};

const quizQuestion2 = {
    question: "Wozu dienen die Trainingsbilder, die ihr hochladet?",
    options: [
        { label: "A) Damit ihr die Bilder in einer Collage anordnen könnt", value: "A" },
        { label: "B) Damit lernt die KI, wie ein Apfel und eine Banane aussehen.", value: "B" },
        { label: "C) Damit die KI die Bilder automatisch nach Datum sortiert.", value: "C" },
    ],
    correctAnswer: "B",
    explanation: "Die Trainingsbilder sind die Daten, die die KI verwendet, um zu 'lernen' und Modelle zu bilden."
};


export default function Level1({ onNextLevel, onRestart, avatarImage, groupName, initialLevelStep }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [model, setModel] = useState<tf.LayersModel | null>(null);
    const [labels, setLabels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [cameraActive, setCameraActive] = useState(false);
    const [videoReady, setVideoReady] = useState(false);

    // Steuert den aktuellen "Schritt" im Level
    // 0: Initialer Einführungstext (Frame 7)
    // 1: Anweisung zur Kategorien-Erstellung/Auswahl (entspricht Frame 16/17, bevor Bilder aufgenommen werden)
    // 2: Anweisung zur Bildaufnahme (entspricht Frame 17/23, während Bilder aufgenommen werden)
    // 3: "Wusstet ihr schon?" + Quiz (Frame 27)
    const [levelStep, setLevelStep] = useState(initialLevelStep ?? 0);
    const [quizCompleted, setQuizCompleted] = useState(false); // Neuer State für den Quiz-Abschluss

    const [categories, setCategories] = useState([
        { name: "Apfel", value: "apfel", count: 0 },
        { name: "Banane", value: "banane", count: 0 },
    ]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const [categorySnapshots, setCategorySnapshots] = useState<CategorySnapshots>({
        apfel: [],
        banane: [],
    });

    const REQUIRED_IMAGES_PER_CATEGORY = 5;

    const allCategoriesCompleted = categories.every(
        (cat) => cat.count >= REQUIRED_IMAGES_PER_CATEGORY
    );

    const [showLearningText, setShowLearningText] = useState(false);
    const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);

    useEffect(() => {
        async function loadModelAndMetadata() {
            setLoading(true);
            try {
                const loadedModel = await tf.loadLayersModel(MODEL_URL);
                setModel(loadedModel);
                const metadataResponse = await fetch(METADATA_URL);
                const metadata = await metadataResponse.json();
                setLabels(metadata.labels);
            } catch (err) {
                console.error("Fehler beim Laden des Modells oder der Metadaten", err);
            } finally {
                setLoading(false);
            }
        }

        loadModelAndMetadata();
    }, []);

    useEffect(() => {
        if (!cameraActive) {
            setVideoReady(false);
            return;
        }

        async function setupCamera() {
            if (!videoRef.current) return;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play();
                        setVideoReady(true);
                    }
                };
            } catch (err) {
                console.error("Fehler beim Zugriff auf die Kamera:", err);
                setVideoReady(false);
            }
        }

        setupCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [cameraActive]);

    async function captureAndClassify() {
        if (!model || !videoRef.current || !canvasRef.current || !selectedCategory || !videoReady || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
            console.warn("Kann kein Bild aufnehmen: Modell nicht geladen, Kamera nicht bereit oder Video-Dimensionen 0.");
            return;
        }

        const currentSnapshots = categorySnapshots[selectedCategory] || [];
        if (currentSnapshots.length >= REQUIRED_IMAGES_PER_CATEGORY) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0);

        const imageSrc = canvas.toDataURL("image/png");

       /*  let imgTensor = tf.browser.fromPixels(canvas);
        imgTensor = tf.image.resizeBilinear(imgTensor, [224, 224]).div(255).expandDims(0);

        const predictionTensor = model.predict(imgTensor) as tf.Tensor;
        const predictionData = await predictionTensor.data();
        tf.dispose([imgTensor, predictionTensor]);

        const predictions = labels.map((label, i) => ({
            label,
            confidence: predictionData[i],
        })); */

        setCategorySnapshots((prev) => ({
            ...prev,
            [selectedCategory]: [...(prev[selectedCategory] || []), { imageSrc, predictions: [] }],
        }));

        setCategories((prevCategories) =>
            prevCategories.map((cat) =>
                cat.value === selectedCategory
                    ? { ...cat, count: cat.count + 1 }
                    : cat
            )
        );
    }

    const handleCategoryClick = (categoryValue: string) => {
        setSelectedCategory(categoryValue);
        setCameraActive(true);
        setLevelStep(2); // In Phase 2: Bildaufnahme
    };

    const handleQuizComplete = (passed: boolean) => {
        setQuizCompleted(passed);
        setLevelStep(4); // Go to final step after quiz
    };

    // Helper for consistent button row styling
    const buttonRowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginTop: '20px',
        gap: '10px',
    };

    useEffect(() => {
        if (allCategoriesCompleted && !showLearningText) {
            setShowLearningText(true);
        }
    }, [allCategoriesCompleted, showLearningText]);

    const cardContainerStyle: React.CSSProperties = {
        width: 'calc(50% - 10px)', // <-- was 'calc(50% - 20px)'
        minWidth: '320px',         // <-- add if you want a minimum width
        height: '350px',
        perspective: '1000px',
        position: 'relative',
        marginBottom: '20px',
    };

    return (
        <div style={{
            fontFamily: 'Nunito, sans-serif',
            backgroundColor: '#f7f9fc',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '30px',
            gap: '30px',
            position: 'relative' // Add for absolute positioning
        }}>

            <div style={{
                flexGrow: 1,
                backgroundColor: '#fff',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                maxWidth: '900px', // <-- Make it wider
                margin: '0 auto',
            }}>
                {/* Restart Button top right INSIDE the white card, styled like Level 1 */}
                <button
                    onClick={onRestart}
                    style={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        zIndex: 10,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    🔄 Level neu starten
                </button>

                {/* Header-Bereich mit Avatar und Level-Titel */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid #4c84ff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        marginRight: '20px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <img src={avatarImage} alt="Team Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>

                    <div style={{ flexGrow: 1 }}>
                        <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '10px' }}>
                            Level 1: Wie KIKI lernt
                        </h2>
                        <p style={{ fontSize: '1.4em', color: '#555' }}>
                            Nehmt {REQUIRED_IMAGES_PER_CATEGORY} Bilder pro Kategorie auf!
                        </p>
                    </div>
                </div>

                {/* Phasensteuerung basierend auf levelStep */}
                {levelStep === 0 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '20px',
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        maxWidth: '900px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        marginTop: '30px',
                    }}>
                        <h3 style={{ fontSize: '1.6em', color: '#333', marginBottom: '10px' }}>Hallo {groupName ? groupName + "! 😊" : "!"}</h3>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Ich habe viele Bilder gefunden, aber ich brauche deine Hilfe, um zu erkennen, was darauf zu sehen ist – zum Beispiel ‚Hund‘ 🐶 oder ‚Apfel‘ 🍏. Das Einordnen in solche Gruppen nennt man Kategorisieren. So lerne ich, Dinge zu unterscheiden!
                        </p>
                        <div style={buttonRowStyle}>
                            <div>
                                {/* No "Zurück" button on very first step */}
                            </div>
                            <button
                                onClick={() => setLevelStep(1)}
                                style={{
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 1 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '20px',
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        maxWidth: '900px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        marginTop: '30px',
                    }}>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Klickt auf die Kategorien ”Apfel” und ”Banane”, um sie zu öffnen und Bilder aufzunehmen. Haltet einen Apfel und danach eine Banane vor die Kamera und klickt auf “Bild aufnehmen”. 🍏🍌
                            <br />
                            Wenn ihr bereit seid, klickt “Weiter”.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(Math.max(0, levelStep - 1))}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button
                                onClick={() => setLevelStep(2)}
                                style={{
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {/* Anzeige der Kategorien und des Aufnahmebereichs nur, wenn levelStep 2 ist und noch nicht alle Bilder gemacht wurden */}
                {levelStep === 2 && !showLearningText && (
                    <>
                        {/* Kategorien-Auswahl */}
                        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '30px', marginBottom: '40px' }}>
                            {categories.map((cat) => (
                                <button
                                    key={cat.value}
                                    onClick={() => handleCategoryClick(cat.value)}
                                    style={{
                                        padding: '20px 30px',
                                        border: `3px solid ${selectedCategory === cat.value ? '#4CAF50' : '#E0E0E0'}`,
                                        borderRadius: '15px',
                                        backgroundColor: selectedCategory === cat.value ? '#E8F5E9' : '#FFFFFF',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minWidth: '150px',
                                        opacity: loading ? 0.6 : 1,
                                        outline: 'none',
                                    }}
                                    disabled={loading}
                                >
                                    {/* Icon above the name */}
                                    <span style={{ fontSize: '2.5em', marginBottom: '5px' }}>
                                        {cat.name === "Apfel" ? "🍏" : cat.name === "Banane" ? "🍌" : ""}
                                    </span>
                                    <span style={{ fontSize: '2em', fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>
                                        {cat.name}
                                    </span>
                                    <span style={{ fontSize: '1.2em', color: '#666' }}>
                                        {cat.count}/{REQUIRED_IMAGES_PER_CATEGORY} Bilder
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Allgemeine Anweisung für Bildaufnahme */}
                        <div style={{
                            backgroundColor: '#e6f7ff',
                            border: '2px solid #91d1ff',
                            borderRadius: '10px',
                            padding: '25px',
                            marginBottom: '30px',
                            textAlign: 'left',
                        }}>
                            <h3 style={{ fontSize: '1.6em', color: '#333', marginBottom: '15px' }}>Jetzt seid ihr dran! 🫵🏻</h3>
                            <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                                Klickt nun auf die Kategorien und trainiert mich jeweils mit {REQUIRED_IMAGES_PER_CATEGORY} Bildern pro Kategorie.
                                <br />Zeigt mir eure besten Schnappschüsse! 📷
                            </p>
                          
                            <button
                                onClick={() => setLevelStep(Math.max(0, levelStep - 1))}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                    justifyContent: 'start',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                        </div>
                        
                    </>
                )}

                {/* Show learning text and quiz start button */}
                {showLearningText && levelStep === 2 && (
                    <div style={{
                        backgroundColor: '#e6f7ff',
                        border: '2px solid #91d1ff',
                        borderRadius: '10px',
                        padding: '25px',
                        marginBottom: '30px',
                        textAlign: 'left',
                    }}>
                        <h3 style={{ fontSize: '1.8em', color: '#333', marginBottom: '15px' }}>Wusstet ihr schon?</h3>
                        <h4 style={{ fontSize: '1.5em', color: '#555', marginBottom: '10px' }}>Wie lernt eine KI?</h4>
                        <p style={{ fontSize: '1.2em', color: '#666', lineHeight: '1.6' }}>
                            Ich lerne, wenn ich ganz viele Bilder sehe! Wenn ihr mir oft zeigt, was ein Apfel ist, merke ich mir: So sieht ein Apfel aus! Dann kann ich auch neue Apfel-Bilder erkennen. Das nennt man Training. So ähnlich wie bei Sportübungen. 🏃‍➡️ Ihr Menschen lernt schnell, aber ich brauche gaaaaanz viele Beispiele, um den Unterschied zu erkennen.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => {
                                    setShowLearningText(false);
                                    setLevelStep(prev => Math.max(1, prev - 1));
                                }}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button
                                onClick={() => setLevelStep(3)}
                                style={{
                                    backgroundColor: '#4c84ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {/* Quiz-Komponente anzeigen, wenn levelStep 3 ist */}
                {levelStep === 3 && (
                 <div style={{ width: '100%' }}>
                <CheckupQuiz
                  question1={quizQuestion1}
                  question2={quizQuestion2}
                  onQuizComplete={handleQuizComplete}
                  onBack={() => setLevelStep(2)}
                     />
                 </div>
)}

                {/* Neues Level Abschluss-Feedback, wenn levelStep 4 ist */}
                {levelStep === 4 && (
                <div style={{
                    backgroundColor: '#e6f7ff',
                    border: '2px solid #91d1ff',
                    borderRadius: '10px',
                    padding: '25px',
                    marginTop: '30px',
                    width: '100%',
                    boxSizing: 'border-box', // Ensures padding is included in width
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '20px',
    }}>
                        <h3 style={{ fontSize: '1.6em', color: '#333', marginBottom: '10px' }}>
                            Super gemacht, Gruppe "{groupName}"! 😊
                        </h3>
                        <p style={{ fontSize: '1.3em', color: '#555', lineHeight: '1.6' }}>
                            Jetzt weiß ich bereits ungefähr, was Äpfel und Bananen sind.<br />
                            Mein Training ist nun abgeschlossen.<br />
                            Drückt “Weiter”, sobald ihr bereit für Level 2 seid.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(3)}
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button
                                onClick={onNextLevel}
                                style={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 25px',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    transition: 'background-color 0.2s ease',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#45a049'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4caf50'}
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {/* Kamera Overlay (unabhängig von levelStep, aber nur wenn cameraActive ist) */}
                {cameraActive && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.8)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                        flexDirection: "column",
                        color: "white",
                    }}>
                        <h2 style={{color: 'white', marginBottom: '15px'}}>Bilder für "{selectedCategory}" aufnehmen</h2>
                        <video
                            ref={videoRef}
                            style={{ border: "3px solid white", borderRadius: '10px', maxWidth: '100%', maxHeight: '70vh' }}
                            autoPlay
                            muted
                            playsInline
                        />
                        <p style={{ marginTop: 20, fontSize: '1.2em' }}>
                            Noch {REQUIRED_IMAGES_PER_CATEGORY - (categorySnapshots[selectedCategory || '']?.length || 0)} Bild(er) aufnehmen
                        </p>
                        <div style={{ marginTop: 20 }}>
                            <button
                                onClick={captureAndClassify}
                                disabled={ (categorySnapshots[selectedCategory || '']?.length || 0) >= REQUIRED_IMAGES_PER_CATEGORY || !videoReady}
                                style={{
                                    backgroundColor: '#5cb85c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '10px 20px',
                                    cursor: ((categorySnapshots[selectedCategory || '']?.length || 0) >= REQUIRED_IMAGES_PER_CATEGORY || !videoReady) ? 'not-allowed' : 'pointer',
                                    fontSize: '1em',
                                    marginRight: '10px',
                                    opacity: ((categorySnapshots[selectedCategory || '']?.length || 0) >= REQUIRED_IMAGES_PER_CATEGORY || !videoReady) ? 0.6 : 1,
                                }} onMouseOver={e => !((categorySnapshots[selectedCategory || '']?.length || 0) >= REQUIRED_IMAGES_PER_CATEGORY || !videoReady) && (e.currentTarget.style.backgroundColor = '#4cae4c')} onMouseOut={e => !((categorySnapshots[selectedCategory || '']?.length || 0) >= REQUIRED_IMAGES_PER_CATEGORY || !videoReady) && (e.currentTarget.style.backgroundColor = '#5cb85c')}>
                                Bild aufnehmen ({ (categorySnapshots[selectedCategory || '']?.length || 0)}/{REQUIRED_IMAGES_PER_CATEGORY})
                            </button>
                            <button onClick={() => setCameraActive(false)} style={{
                                backgroundColor: '#d9534f',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                cursor: 'pointer',
                                fontSize: '1em',
                            }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#c9302c'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#d9534f'}>
                                Schließen
                            </button>
                        </div>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: "none" }} />

                {/* Erfasste Bilder nach Kategorien nur anzeigen, wenn levelStep NICHT 3 ist (also NICHT das Quiz läuft) */}
                {levelStep === 2 && ( // Zeigt die Schnappschüsse an, solange das Quiz nicht aktiv ist
                    <>
                        <h2 style={{ color: '#333', fontSize: '1.8em', marginTop: '30px', marginBottom: '20px' }}>Eure Schnappschüsse: 📷</h2>
                        {categories.map((cat) => (
                            <div key={cat.value} style={{ marginBottom: '30px' }}>
                                <h3 style={{ fontSize: '1.5em', color: '#4c84ff', marginBottom: '15px' }}>
                                    {cat.name} Bilder ({cat.count}/{REQUIRED_IMAGES_PER_CATEGORY})
                                </h3>
                                {cat.count === 0 && <p style={{ color: '#777' }}>Noch keine Bilder für {cat.name} aufgenommen.</p>}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 15 }}>
                                    {(categorySnapshots[cat.value] || []).map((s, i) => (
                                        <div key={i} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px', width: '150px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
                                            <img src={s.imageSrc} alt={`${cat.name} Snapshot ${i + 1}`} style={{ maxWidth: '100%', borderRadius: '5px', marginBottom: '5px' }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </>
                )}


                
            </div>
        </div>
    );
}